import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Phone, Globe, Shield, Zap, HeadphonesIcon, TrendingUp,
  ArrowRight, Check, MessageCircle, Users, Server, Award
} from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const HomePage = () => {
  const [stats, setStats] = useState({
    total_routes: "53,000+",
    total_countries: "235+",
    active_customers: "500+",
    uptime: "99.9%"
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/stats`);
        setStats(res.data);
      } catch (e) {
        console.error("Failed to fetch stats", e);
      }
    };
    fetchStats();
  }, []);

  const services = [
    {
      icon: Phone,
      title: "VoIP/SIP Accounts",
      description: "High-quality voice services with flexible SIP trunk solutions for businesses of all sizes."
    },
    {
      icon: Server,
      title: "Hosted PBX",
      description: "Cloud-based phone systems with advanced features, no hardware required."
    },
    {
      icon: Globe,
      title: "A-Z Termination",
      description: "Comprehensive voice termination to 235+ countries with competitive rates."
    },
    {
      icon: HeadphonesIcon,
      title: "Call Center Routes",
      description: "Specialized routes engineered for high CPM and low ACD call center traffic."
    }
  ];

  const features = [
    { icon: Shield, text: "Full CLI Delivery" },
    { icon: Zap, text: "Low PDD" },
    { icon: TrendingUp, text: "High ASR/ACD" },
    { icon: Award, text: "24/7 Support" }
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center hero-pattern overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-red-50 opacity-90" />
        
        {/* Decorative elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-red-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-red-500/5 rounded-full blur-3xl" />
        
        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div 
              className="lg:col-span-7"
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Zap className="w-4 h-4" />
                Premium VoIP Services
              </motion.div>
              
              <motion.h1 variants={fadeInUp} className="text-5xl md:text-7xl font-bold text-slate-900 leading-tight mb-6">
                Global Voice
                <span className="block text-red-600">Termination</span>
                Made Simple
              </motion.h1>
              
              <motion.p variants={fadeInUp} className="text-lg md:text-xl text-slate-600 mb-8 max-w-xl leading-relaxed">
                Connect to 235+ countries with crystal-clear voice quality. 
                Competitive rates, reliable routes, and 24/7 support for your business.
              </motion.p>
              
              <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
                <a href="/portal" rel="noopener noreferrer">
                  <Button className="bg-red-600 hover:bg-red-700 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-red-500/30 transition-all" data-testid="hero-signup-btn">
                    Get Started <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
                <Link to="/rates">
                  <Button variant="outline" className="rounded-full px-8 py-6 text-lg font-medium border-slate-300 hover:border-red-500 hover:text-red-600" data-testid="hero-rates-btn">
                    View Rates
                  </Button>
                </Link>
              </motion.div>
              
              <motion.div variants={fadeInUp} className="flex flex-wrap gap-6 mt-10">
                {features.map((feat, i) => (
                  <div key={i} className="flex items-center gap-2 text-slate-600">
                    <feat.icon className="w-5 h-5 text-red-500" />
                    <span className="font-medium">{feat.text}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>
            
            <motion.div 
              className="lg:col-span-5 relative"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="relative h-[400px] md:h-[500px]">
                <img 
                  src="https://images.unsplash.com/photo-1767477665606-a29603e1c479?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxyZWQlMjBhYnN0cmFjdCUyMHRlY2hub2xvZ3klMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3MTE3MjUxMnww&ixlib=rb-4.1.0&q=85"
                  alt="Technology Background"
                  className="w-full h-full object-cover rounded-3xl shadow-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent rounded-3xl" />
                
                {/* Floating stats card */}
                <motion.div 
                  className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                      <Globe className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-slate-900">{stats.total_countries}</p>
                      <p className="text-sm text-slate-500">Countries Covered</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Ticker */}
      <section className="bg-slate-900 py-8 noise-bg" data-testid="stats-section">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { label: "Voice Routes", value: stats.total_routes },
              { label: "Countries", value: stats.total_countries },
              { label: "Active Customers", value: stats.active_customers },
              { label: "Uptime", value: stats.uptime }
            ].map((stat, i) => (
              <motion.div 
                key={i}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <p className="text-3xl md:text-4xl font-bold text-red-500">{stat.value}</p>
                <p className="text-slate-400 mt-1">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-light" data-testid="services-section">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Our Services</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive VoIP solutions tailored to your business needs
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, i) => (
              <motion.div
                key={i}
                className="feature-card group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-red-600 transition-colors">
                  <service.icon className="w-7 h-7 text-red-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{service.title}</h3>
                <p className="text-slate-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-alt" data-testid="why-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                Why Choose <span className="text-red-600">CyberX</span>?
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                We've been delivering quality voice services globally. Our partnerships with Tier 1 carriers ensure premium call quality.
              </p>
              
              <ul className="space-y-4">
                {[
                  "Competitive rates starting from $0.001/min",
                  "24/7 technical support via Telegram",
                  "No minimum commitment required",
                  "Real-time rate updates",
                  "Multiple payment options (USD/EUR)"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-red-600" />
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
              
              <div className="flex gap-4 mt-10">
                <Link to="/pricing">
                  <Button className="bg-red-600 hover:bg-red-700 text-white rounded-full px-6" data-testid="why-pricing-btn">
                    View Plans <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <a href="https://t.me/cyberx007" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="rounded-full px-6 border-slate-300" data-testid="why-telegram-btn">
                    <MessageCircle className="mr-2 w-4 h-4" /> Chat on Telegram
                  </Button>
                </a>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1758876019427-e2e5bf9a92c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHx3b21hbiUyMHdpdGglMjBoZWFkc2V0JTIwc21pbGluZyUyMGluJTIwb2ZmaWNlfGVufDB8fHx8MTc3MTE3MjUwM3ww&ixlib=rb-4.1.0&q=85"
                alt="Support Team"
                className="w-full h-[500px] object-cover rounded-3xl shadow-xl"
              />
              
              {/* Floating card */}
              <motion.div 
                className="absolute -bottom-6 -right-6 bg-slate-900 text-white rounded-2xl shadow-xl p-6 max-w-xs"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <Users className="w-8 h-8 text-red-500" />
                  <span className="text-2xl font-bold">500+</span>
                </div>
                <p className="text-slate-300 text-sm">Active customers trust us for their voice termination needs</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-dark" data-testid="cta-section">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
              Join hundreds of satisfied customers. Start terminating calls today with our competitive rates and reliable routes.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/portal" rel="noopener noreferrer">
                <Button className="bg-red-600 hover:bg-red-700 text-white rounded-full px-8 py-6 text-lg font-semibold" data-testid="cta-signup-btn">
                  Create Free Account <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
              <Link to="/contact">
                <Button variant="outline" className="rounded-full px-8 py-6 text-lg font-medium border-slate-600 text-white hover:bg-white/10" data-testid="cta-contact-btn">
                  Contact Sales
                </Button>
              </Link>
            </div>
            <p className="text-slate-400 mt-6 text-sm">
              Minimum recharge: 100 USD/EUR • No setup fees
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
